import type {
  MemberPasswordUpdatePayload,
  MemberProfileUpdatePayload
} from '~/types/types'
import type { StaffNotificationItem } from '~/repositories/memberRepository'

/**
 * Profile / password / avatar / account-deletion actions for the
 * authenticated user. Backed by /api/auth/* endpoints (profile, password,
 * account), /api/upload (type=avatar), and /api/me/notifications* (the
 * bell in AppShell.vue — see StaffNotificationController on the backend).
 *
 * messages_unread_count has no backing system yet (no separate
 * message-thread inbox) — kept as a permanent 0 so NotificationsPanel and
 * AppShell's badge logic don't need special-casing.
 */
export const useMember = () => {
  const auth = useAuth()
  const repository = useMemberRepository()
  const uploadRepository = useUploadRepository()

  const loading = useState<boolean>('member-loading', () => false)
  const avatarUploading = useState<boolean>('member-avatar-uploading', () => false)

  const notifications = useState<StaffNotificationItem[]>('member-notifications', () => [])
  const notificationsMeta = useState('member-notifications-meta', () => ({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0
  }))
  const unreadCount = useState<number>('member-unread-count', () => 0)
  const messagesUnreadCount = useState<number>('member-messages-unread-count', () => 0)

  const withLoading = async <T>(fn: () => Promise<T>): Promise<T> => {
    loading.value = true
    try {
      return await fn()
    } finally {
      loading.value = false
    }
  }

  const updateProfile = async (payload: MemberProfileUpdatePayload) => withLoading(async () => {
    const updated = await repository.updateProfile(payload)
    if (auth.user.value) {
      auth.user.value = { ...auth.user.value, ...updated }
    } else {
      auth.user.value = updated
    }
    return auth.user.value
  })

  const updatePassword = (payload: MemberPasswordUpdatePayload) => withLoading(async () => {
    await repository.updatePassword(payload)
  })

  const uploadAvatar = async (file: File): Promise<string | null> => {
    avatarUploading.value = true
    try {
      const { url } = await uploadRepository.upload(file, 'avatar')
      if (auth.user.value) {
        auth.user.value = { ...auth.user.value, avatar: url }
      }
      return url
    } finally {
      avatarUploading.value = false
    }
  }

  const deleteAccount = async () => withLoading(async () => {
    await repository.deleteAccount()
    await auth.logout()
    await navigateTo('/')
  })

  const loadNotifications = async (params: { page?: number, per_page?: number } = {}) => {
    const page = await repository.loadNotifications(params)
    notifications.value = page.data
    notificationsMeta.value = {
      current_page: page.current_page,
      last_page: page.last_page,
      per_page: page.per_page,
      total: page.total
    }
    return { data: page.data, meta: notificationsMeta.value }
  }

  const loadUnreadCount = async () => {
    const counts = await repository.unreadCounts()
    unreadCount.value = counts.unread_count
    messagesUnreadCount.value = counts.messages_unread_count
    return counts.unread_count
  }

  // Separate stub call site in AppShell.vue's polling — both counts come
  // from the same backend call, so just reuse it rather than adding a
  // second endpoint for a number that's always 0 today.
  const loadMessagesUnreadCount = async () => {
    const counts = await repository.unreadCounts()
    messagesUnreadCount.value = counts.messages_unread_count
    return counts.messages_unread_count
  }

  const markRead = async (id: number | string) => {
    const numericId = Number(id)
    await repository.markNotificationRead(numericId)
    const item = notifications.value.find(n => n.id === numericId)
    if (item && !item.read_at) {
      item.read_at = new Date().toISOString()
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    }
  }

  const markAllRead = async () => {
    await repository.markAllNotificationsRead()
    const now = new Date().toISOString()
    notifications.value = notifications.value.map(n => ({ ...n, read_at: n.read_at ?? now }))
    unreadCount.value = 0
  }

  return {
    loading,
    avatar_uploading: avatarUploading,
    notifications,
    notifications_meta: notificationsMeta,
    unread_count: unreadCount,
    messages_unread_count: messagesUnreadCount,

    update_profile: updateProfile,
    update_password: updatePassword,
    upload_avatar: uploadAvatar,
    delete_account: deleteAccount,

    load_notifications: loadNotifications,
    load_unread_count: loadUnreadCount,
    load_messages_unread_count: loadMessagesUnreadCount,
    mark_read: markRead,
    mark_all_read: markAllRead
  }
}
